import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModulePublishingState } from '../../../../../../../@types/module-publishing-state';
import { fetchTocNodes, Node, NodeWithoutDescription } from '../../../../../../../api/table-of-content';
import { getUserMaterial, UserMaterial } from '../../../../../../../api/user-material';
import { flattenNodes } from '../../../../../../../utils/tocNode';
import { getModulesContainingUserMaterial } from '../api';
import { NodeWithMaterial } from '../components/NodeBranch';
import { sortModulesByCatalogName, sortSubModulesByNiceName } from '../util/sorting';

interface SuperModule {
  id: string;
  catalogName: string;
  niceName: string;
  books: SubModule[];
  publishingState: ModulePublishingState;
  weight: number;
  subjectSlug: string;
}

export interface SubModule {
  id: string;
  niceName: string;
  weight: number;
}

export default function useModulesWithUserMaterial(selectedSubModule: SubModule | null) {
  const { t } = useTranslation();
  const [modules, setModules] = useState<SuperModule[]>([]);

  const [nodesWithMaterial, setNodesWithMaterial] = useState<NodeWithMaterial[]>([]);
  const [allNodesWithMaterialById, setAllNodesWithMaterialById] = useState<Record<string, NodeWithMaterial>>({});
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isLoadingTocNodes, setIsLoadingTocNodes] = useState(true);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const modules = await getModulesContainingUserMaterial();
        const sortedModules = sortModulesByCatalogName(modules).map(module => ({
          ...module,
          books: sortSubModulesByNiceName(module.books),
        }));

        setModules(sortedModules);
        if (!sortedModules.length) {
          setIsLoadingTocNodes(false);
        }
      } catch {
        setModules([]);
        setIsLoadingTocNodes(false);
      } finally {
        setIsLoadingModules(false);
      }
    };

    fetchModules();
  }, []);

  useEffect(() => {
    function buildMaterialNodeTree(
      toc: NodeWithoutDescription[],
      materialByNode: Record<string, UserMaterial[]>,
    ): NodeWithMaterial[] {
      function transformNode(node: NodeWithoutDescription, pathStack: string[]): NodeWithMaterial | null {
        const newPath = [...pathStack, node.id];
        const children =
          node.nodes?.map(node => transformNode(node, newPath)).filter((n): n is Node => n !== null) || [];

        const hasMaterials = Boolean(materialByNode[node.id]?.length);

        if (hasMaterials || children.length) {
          return {
            ...node,
            nodes: children,
            material: materialByNode[node.id] || [],
            path: pathStack,
          };
        }

        return null;
      }

      return toc.map(node => transformNode(node, [])).filter((n): n is NodeWithMaterial => n !== null);
    }

    async function getTocNodes() {
      if (!selectedSubModule) return;

      try {
        const [{ data: tocNodes }, { data: userMaterial }] = await Promise.all([
          fetchTocNodes(selectedSubModule.id, true, true),
          getUserMaterial(selectedSubModule.id, { includeNodeHierarchy: true }),
        ]);

        const userMaterialGroupedByNode = groupBy(userMaterial, 'node');
        const nodeTree = buildMaterialNodeTree(tocNodes, userMaterialGroupedByNode);
        const allNodesById = keyBy(flattenNodes(nodeTree), 'id');

        setNodesWithMaterial(nodeTree);
        setAllNodesWithMaterialById(allNodesById);
      } catch (e) {
        console.error('Error fetching TOC nodes:', e);
      } finally {
        setIsLoadingTocNodes(false);
      }
    }

    getTocNodes();
  }, [selectedSubModule, t]);

  return { modules, nodesWithMaterial, allNodesWithMaterialById, isLoading: isLoadingModules || isLoadingTocNodes };
}
